import React, { Fragment, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { PropTypes } from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  TableHead,
  TableCell,
  TableRow,
  Table,
  TableBody
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import sortBy from 'lodash/sortBy';
import sortedUniqBy from 'lodash/sortedUniqBy';
import { useSkillLevels } from '~/modules/common/hooks';
import useFocusListElementEffect from '~/modules/common/hooks/useFocusListElementEffect';
import NoDataItem from '~/modules/common/components/NoDataItem';
import { SkillsSelectDropdown, SkillsForm, SkillCategory } from '../Skills';
import { useSkillRemovalFocus } from './hooks';

const useStyles = makeStyles(theme => ({
  select: {
    zIndex: 2,
    position: 'sticky !important',
    left: 0
  },
  noSkill: { paddingTop: theme.spacing(2) },
  error: { color: theme.palette.error.main },
  table: { marginTop: theme.spacing(1) },
  tableHeadScreenReaderOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
  }
}));

const getSkillsByCategory = (categories, skills) => {
  const getSkillsForCategory = category => ({
    category,
    skills: skills.filter(skill => skill.category.id === category.id)
  });

  return categories.map(getSkillsForCategory);
};

export const AddSkills = ({
  skills,
  setFieldValue,
  required,
  variant,
  className,
  isResourceRequestSkillRow,
  customLabel
}) => {
  const classes = useStyles();

  const { skillLevels, maxSkillLevel, isSkillLoading } = useSkillLevels();

  const {
    fallbackElementRef,
    getMatchingRowElementRef,
    setNextFocus
  } = useFocusListElementEffect();

  const categories = useMemo(
    () =>
      sortedUniqBy(
        sortBy(skills, skillItem => skillItem.category.displayText),
        skillItem => skillItem.category.id
      ).map(option => option.category),
    [skills]
  );

  const onSkillsSelected = useCallback(
    event => {
      setFieldValue(
        'skills',
        sortBy(
          event.map(e => ({
            ...e,
            skillLevel: e.skillLevel || skillLevels[0]
          })),
          skillItem => skillItem.displayText
        )
      );
    },
    [setFieldValue, skillLevels]
  );

  const onSkillAdd = useCallback(
    event => onSkillsSelected([...skills, event]),
    [skills, onSkillsSelected]
  );

  const {
    setNextFocusAfterSkillRemoval,
    setNextFocusAfterCategoryRemoval
  } = useSkillRemovalFocus({
    skillsByCategory: getSkillsByCategory(categories, skills),
    setNextFocus
  });

  const onSkillRatingUpdate = ({ ratingIndex, skillAssignment }) => {
    const selectedSkillLevel = skillLevels.find(
      level => level.rating === ratingIndex
    );
    const filteredSkills = skills.map(skillItem => ({
      ...skillItem,
      skillLevel:
        skillItem.id === skillAssignment.id
          ? selectedSkillLevel
          : skillItem.skillLevel
    }));

    setFieldValue('skills', filteredSkills);
  };

  const onCategoryRemove = useCallback(
    category => {
      const filteredSkills = skills.filter(
        skillItem => skillItem.category.id !== category.id
      );

      setFieldValue('skills', filteredSkills);
      setNextFocusAfterCategoryRemoval(category, filteredSkills);
    },
    [skills, setFieldValue, setNextFocusAfterCategoryRemoval]
  );

  const onSkillRemove = useCallback(
    skill => {
      const filteredSkills = skills.filter(item => item.id !== skill.id);

      setFieldValue('skills', filteredSkills);
      setNextFocusAfterSkillRemoval(skill, filteredSkills);
    },
    [skills, setFieldValue, setNextFocusAfterSkillRemoval]
  );
  const skillCategoryClasses = useMemo(
    () => ({ tableHeadScreenReaderOnly: classes.tableHeadScreenReaderOnly }),
    [classes.tableHeadScreenReaderOnly]
  );

  return (
    <>
      <SkillsSelectDropdown
        value={skills.map(u => ({
          ...u,
          value: u.uri || u.id,
          label: u.displayText
        }))}
        onChange={onSkillsSelected}
        onSkillAdd={onSkillAdd}
        classes={classes}
        variant={variant}
        className={className}
        customLabel={customLabel}
        inputRef={fallbackElementRef}
      />
      {!isSkillLoading && skills.length > 0 ? (
        <Table size="small" className={classes.table}>
          <TableHead className={classes.tableHeadScreenReaderOnly}>
            <TableRow>
              <TableCell>
                <FormattedMessage id="resourceRequestDrawerDetails.skill" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="resourceRequestDrawerDetails.skillRating" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="resourceRequestDrawerDetails.action" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map(category => (
              <Fragment key={category.id}>
                <TableRow>
                  <SkillCategory
                    category={category}
                    onCategoryRemove={onCategoryRemove}
                    inputRef={getMatchingRowElementRef?.(category.id)}
                    classes={skillCategoryClasses}
                  />
                </TableRow>
                <SkillsForm
                  skills={skills}
                  maxSkillLevel={maxSkillLevel}
                  category={category}
                  onSkillRemove={onSkillRemove}
                  showCertificates={false}
                  onSkillRatingUpdate={onSkillRatingUpdate}
                  isResourceRequestSkillRow={isResourceRequestSkillRow}
                  getMatchingRowElementRef={getMatchingRowElementRef}
                />
              </Fragment>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className={classes.noSkill}>
          <NoDataItem>
            <span
              className={classNames({
                [classes.error]: required
              })}
            >
              <FormattedMessage id="addRoleDialog.noSkills" />
            </span>
          </NoDataItem>
        </div>
      )}
    </>
  );
};

AddSkills.propTypes = {
  skills: PropTypes.array,
  setFieldValue: PropTypes.func,
  required: PropTypes.bool,
  variant: PropTypes.string,
  className: PropTypes.string,
  isResourceRequestSkillRow: PropTypes.bool,
  customLabel: PropTypes.string
};

export default AddSkills;
